import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  BlackButtonComponent,
  ImageModal,
  WhiteButtonComponent,
} from "components";
import useMerchantProfileScreen from "./hooks/useMerchantProfileScreen";
import ProfileDetailsForm from "../../components/shared/profile-details-form/profile-details-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { appendDetails } from "services/MerchantHostApi";
import { useEffect, useState } from "react";
import { RootState } from "store";
import { updateInitialState } from "slice/merchantSlice";
import {
  containsPropertyMerchant,
  getChangedFields,
  arrayBufferToFile,
} from "../../utils/index";
import { getImage } from "services/createTripService";
import {
  uploadMultipleImages,
  uploadSingleImage,
} from "store/thunks/imageThunks";
import { useNavigate } from "react-router";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ProfileImage } from "assets/index";
interface Image {
  file?: Blob | MediaSource;
  preview: string;
  fileName?: string;
  mimetype?: string;
}

const MerchantProfileDetailsScreen = () => {
  const { formik } = useMerchantProfileScreen();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(formik.values);
  const [showExampleImage, setShowExampleImage] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;
  const folderName = userDetails.searchIdKey + "" + userDetails.searchId;

  const uploadImage = async (image: Image, fileName: string) => {
    if (!image?.file) return null;
    try {
      const data = {
        file: image.file as File,
        folderName,
        fileName: "cover",
        mimetype: image.mimetype,
      };
      const response = await dispatch(uploadSingleImage(data) as any);
      if (response.meta.requestStatus === "fulfilled") {
        return response.payload;
      } else {
        return null;
      }
    } catch (error) {
      toast.error("Error uploading image.");
      return null;
    }
  };

  const uploadCoverImages = async () => {
    if (!formik.values.coverPhotos.length) return [];

    try {
      // Extract existing sequence numbers
      const existingNumbers = formik.values.coverPhotos
        .map((img) => parseInt(img.fileName as string)) // Convert file names to numbers
        .filter((num) => !isNaN(num)) // Remove invalid values
        .sort((a, b) => a - b); // Sort numerically

      // Find the next available number (avoiding overwrite)
      let nextNumber =
        existingNumbers.length > 0
          ? existingNumbers[existingNumbers.length - 1] + 1
          : 1;

      const files = formik.values.coverPhotos.map((image) => {
        if (image.fileName && !isNaN(parseInt(image.fileName))) {
          // Keep the original number if the image was already stored
          return {
            file: image.file as File,
            folderName,
            fileName: image.fileName,
            mimetype: image.mimetype,
          };
        } else {
          // Assign new sequential number for new uploads
          const sequenceNumber = String(nextNumber++).padStart(3, "0");
          return {
            file: image.file as File,
            folderName,
            fileName: sequenceNumber,
            mimetype: image.mimetype,
          };
        }
      });

      const response = await dispatch(uploadMultipleImages({ files }) as any);
      if (response.meta.requestStatus === "fulfilled") {
        return response.payload;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Error uploading Cover Images.");
      return [];
    }
  };

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }
    setLoading(true);

    const updatedLogo = await uploadImage(
      formik.values.logo,
      formik.values.logo.fileName as string
    );
    const updatedCoverPhotos = await uploadCoverImages();

    try {
      const data: any = {
        merchantProfileDetails: {
          ...formik.values,
          logo: updatedLogo || formik.values.logo,
          coverPhotos: updatedCoverPhotos.length
            ? updatedCoverPhotos
            : formik.values.coverPhotos,
          updatedItems: getChangedFields(initialValues, formik.values),
        },
      };

      const val = containsPropertyMerchant(
        userDetails,
        "merchantProfileDetails"
      );

      if (val === "UPDATE") {
        data.kycStatus = "UPDATE";
      } else if (val === "TRUE") {
        data.kycStatus = "COMPLETED";
      }

      const response = await appendDetails(
        userDetails.userType,
        userDetails.userId,
        data
      );

      if (response?.message === "Item updated successfully") {
        toast.success("Merchant details updated successfully!");
        dispatch(updateInitialState(response?.updatedItem));
        setInitialValues(formik.values);
        navigate("/bank-details");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to update details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const convertFetchedImagesToFiles = async () => {
      if (formik.values.coverPhotos.length) {
        const processedImages = await Promise.all(
          formik.values.coverPhotos.map(async (img) => {
            if (img.file) return img;
            const buffer = await getImage(folderName, img.fileName as string);

            return {
              ...img,
              file: arrayBufferToFile(buffer, img.fileName as string),
            };
          })
        );
        formik.setFieldValue("coverPhotos", processedImages);
      }
      if (formik.values.logo.preview) {
        const buffer = await getImage(
          folderName,
          formik.values.logo.fileName as string
        );
        formik.setFieldValue("logo", {
          ...formik.values.logo,
          file: arrayBufferToFile(
            buffer,
            formik.values.logo.fileName as string
          ),
        });
      }
    };
    convertFetchedImagesToFiles();
  }, []);

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      {/* Header Section - Made Responsive */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
        justifyContent="space-between"
        gap={isMobile ? 2 : 0}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} mb={1}>
            Merchant Profile Details
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "subtitle1"}
            fontFamily={"Inter"}
            fontWeight={"500"}
            fontSize={isMobile ? 14 : 16}
            display={"block"}
            ml={0}
          >
            (This will be visible to users on the app)
          </Typography>
        </Box>
        <WhiteButtonComponent
          btnText="See Example"
          onClick={() => setShowExampleImage(true)}
          sx={{
            cursor: "pointer",
            marginLeft: isMobile ? 0 : "auto",
            width: isMobile ? "100%" : "auto",
            mt: isMobile ? 1 : 0,
          }}
        />
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          mb: 3,
        }}
      >
        <Box
          component="ul"
          sx={{ pl: { xs: 1, sm: 3 }, mb: 3, "& li": { mb: 1.5 } }}
        >
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            gap={1}
            mb={2}
          >
            <Box component="span" fontWeight="600" color="primary.main">
              Merchants Must Provide:
            </Box>
            <Box
              component="ul"
              sx={{
                pl: { xs: 1, sm: 2.5 },
                listStyleType: "circle",
                mt: isMobile ? 1 : 0,
              }}
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              gap={isMobile ? 0.5 : 1}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Profile picture
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Cover photos
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Company bio/description
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            mb={isMobile ? 2 : 0}
          >
            <Box component="span" fontWeight="600" color="primary.main">
              Customizable Sections (Up to 3):
            </Box>
            <Box
              sx={{ pl: { xs: 1, sm: 2.5 }, mt: isMobile ? 1 : 0 }}
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              gap={isMobile ? 0.5 : 1}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Section Heading
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Sub-heading
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant={isMobile ? "body2" : "body1"}>
                  Detailed Description
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Typography
          variant={isMobile ? "body2" : "body1"}
          sx={{ my: 2, fontStyle: "italic", textAlign: "flex-start" }}
        >
          Feel free to highlight your expertise, past experiences, or anything
          that helps users make informed decisions. Make your profile engaging
          and authentic—your next great travel experience starts here!
        </Typography>

        <Typography
          variant="body2"
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 1,
            bgcolor: "warning.light",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmberRoundedIcon
              color="warning"
              fontSize={isMobile ? "small" : "medium"}
            />
            <Box component="span" fontWeight="600">
              Prohibited:
            </Box>
          </Box>
          <Box>
            Sharing contact details, social media links, or direct communication
            methods may lead to account suspension.
          </Box>
        </Typography>
      </Box>

      {/* Profile Form */}
      <ProfileDetailsForm formik={formik} />

      {/* Buttons */}
      <Box
        textAlign={isMobile ? "center" : "right"}
        display="flex"
        justifyContent={isMobile ? "center" : "end"}
        columnGap={2}
      >
        <BlackButtonComponent
          onClick={handleSubmit}
          btnText={loading ? "Submitting..." : "Submit"}
          sx={{
            padding: "6px 40px",
            width: isMobile ? "100%" : "auto",
          }}
          disabled={loading}
        />
      </Box>
      <ImageModal
        open={showExampleImage}
        onClose={() => setShowExampleImage(false)}
      >
        <img
          src={ProfileImage}
          alt="Trip example"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
          }}
        />
      </ImageModal>
    </Box>
  );
};

export default MerchantProfileDetailsScreen;
